import { useState, useEffect } from 'react';
import {
  User,
  Bell,
  Shield,
  AlertCircle,
  Moon,
  Sun,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth, api } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const darkMode = theme === 'dark';
  const [activeTab, setActiveTab] = useState('profile');
  const [notifications, setNotifications] = useState({
    approvals: true,
    reports: true,
    tasks: false,
    marketing: false
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company_name || ''
  });

  // Fetch notification preferences on mount
  useEffect(() => {
    fetchNotificationPrefs();
  }, []);

  const fetchNotificationPrefs = async () => {
    try {
      const res = await api.get('/auth/notification-preferences');
      setNotifications(res.data);
    } catch {
      // use defaults
    }
  };

  const saveNotificationPrefs = async (updated) => {
    setNotificationsLoading(true);
    try {
      await api.put('/auth/notification-preferences', updated);
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Failed to save notification preferences');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationChange = (key, checked) => {
    const updated = { ...notifications, [key]: checked };
    setNotifications(updated);
    saveNotificationPrefs(updated);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {};
      if (profile.name !== (user?.name || '')) payload.name = profile.name;
      if (profile.email !== (user?.email || '')) payload.email = profile.email;
      if (profile.company !== (user?.company_name || '')) payload.company_name = profile.company;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSavingProfile(false);
        return;
      }

      await api.patch('/auth/me', payload);
      toast.success('Profile updated successfully');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Settings</h1>
        <p className="text-slate-600 dark:text-slate-400">
          Manage your account and preferences
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
          <TabsTrigger value="profile" className="rounded-lg">Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-lg">Notifications</TabsTrigger>
          <TabsTrigger value="security" className="rounded-lg">Security</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-500" />
                Profile Information
              </CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    data-testid="profile-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    value={profile.company}
                    onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                    data-testid="profile-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <Input value={user?.role?.replace('_', ' ')} disabled className="capitalize" />
                </div>
              </div>
              <Button className="gds-btn-primary" onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                {savingProfile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                {darkMode ? <Moon className="w-5 h-5 text-indigo-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
                Appearance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Dark Mode</p>
                  <p className="text-sm text-slate-500">Toggle dark theme</p>
                </div>
                <Switch
                  checked={darkMode}
                  onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                  data-testid="dark-mode-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Bell className="w-5 h-5 text-indigo-500" />
                Email Notifications
              </CardTitle>
              <CardDescription>Choose what you want to be notified about</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { key: 'approvals', label: 'Approval Requests', desc: 'When items need your review' },
                { key: 'reports', label: 'Monthly Reports', desc: 'Performance reports and summaries' },
                { key: 'tasks', label: 'Task Updates', desc: 'When tasks are completed or updated' },
                { key: 'marketing', label: 'Marketing & Tips', desc: 'Product updates and SEO tips' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                  <Switch
                    checked={notifications[item.key]}
                    onCheckedChange={(checked) => handleNotificationChange(item.key, checked)}
                    disabled={notificationsLoading}
                    data-testid={`notification-${item.key}-toggle`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-500" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {user?.auth_provider === 'google' && (
                <div className="flex items-start gap-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Your account uses Google sign-in. You can set a password below to also enable email/password login.
                  </p>
                </div>
              )}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                className="gds-btn-primary"
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
                data-testid="change-password-btn"
              >
                {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</> : 'Change Password'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
